import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import {
  Plus, Save, Trash2, Copy, Eye, EyeOff, X, Image, GripVertical, Clock, Search, Palette,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ImageSourceSelector, getBannerSizeGuide } from "@/components/ImageSourceSelector";

const BANNER_TYPES = [
  { value: "homepage", label: "Homepage" },
  { value: "premium", label: "Premium" },
  { value: "announcement", label: "Announcement" },
  { value: "event", label: "Event" },
  { value: "subscription", label: "Subscription" },
  { value: "category", label: "Category" },
  { value: "channel", label: "Channel" },
  { value: "app_download", label: "App Download" },
];

const PLACEMENTS = [
  { value: "homepage_top", label: "Homepage Top" },
  { value: "homepage_middle", label: "Homepage Middle" },
  { value: "homepage_bottom", label: "Homepage Bottom" },
  { value: "live_tv", label: "Live TV" },
  { value: "category_page", label: "Category Page" },
  { value: "channel_page", label: "Channel Page" },
  { value: "subscription_page", label: "Subscription Page" },
  { value: "footer", label: "Footer" },
  { value: "global_top", label: "Global Top" },
  { value: "global_bottom", label: "Global Bottom" },
];

const STATUS_COLORS = {
  published: "bg-green-500/10 text-green-400 border-green-500/30",
  draft: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  scheduled: "bg-blue-500/10 text-blue-400 border-blue-500/30",
};

function SortableBannerCard({ banner, onEdit, onDuplicate, onDelete, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: banner.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const img = banner.desktop_image || banner.image;
  return (
    <div ref={setNodeRef} style={style} className={cn(
      "bg-slate-800/50 border rounded-xl overflow-hidden transition-all",
      isDragging ? "border-blue-500 shadow-lg" : "border-slate-700/50",
      !banner.is_active && "opacity-50"
    )} data-testid={`banner-${banner.id}`}>
      <div className="flex items-stretch">
        <button {...attributes} {...listeners} className="px-3 bg-slate-800/80 cursor-grab active:cursor-grabbing text-slate-600 hover:text-slate-400 touch-none">
          <GripVertical size={16} />
        </button>
        <div className="w-28 h-20 bg-slate-900 flex-shrink-0 relative overflow-hidden">
          {img ? (
            <img src={img.startsWith("/api") ? `${window.location.origin}${img}` : img} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: banner.background_color || "#0F172A" }}>
              <Image size={20} className="text-slate-600" />
            </div>
          )}
        </div>
        <div className="flex-grow p-3 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-semibold text-white truncate">{banner.name || "Untitled Banner"}</h4>
            <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-semibold border", STATUS_COLORS[banner.status] || STATUS_COLORS.draft)}>
              {banner.status}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <span className="px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400">{banner.type}</span>
            <span className="px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400">{banner.placement?.replace(/_/g, " ")}</span>
          </div>
          {(banner.start_date || banner.end_date) && (
            <div className="flex items-center gap-1 mt-1 text-[10px] text-slate-600">
              <Clock size={10} />
              {banner.start_date && <span>From: {new Date(banner.start_date).toLocaleDateString()}</span>}
              {banner.end_date && <span>Until: {new Date(banner.end_date).toLocaleDateString()}</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 px-3 flex-shrink-0">
          <button onClick={() => onToggle(banner)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400" data-testid={`banner-toggle-${banner.id}`}>
            {banner.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
          <button onClick={() => onEdit(banner)} className="p-1.5 rounded-lg hover:bg-slate-700 text-blue-400 text-xs font-medium" data-testid={`banner-edit-${banner.id}`}>
            Edit
          </button>
          <button onClick={() => onDuplicate(banner)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400"><Copy size={14} /></button>
          <button onClick={() => onDelete(banner)} className="p-1.5 rounded-lg hover:bg-red-900/30 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
}

const emptyBanner = {
  name: "", type: "homepage", placement: "homepage_middle",
  title_fr: "", title_en: "", description_fr: "", description_en: "",
  desktop_image: "", tablet_image: "", mobile_image: "", image: "", icon: "",
  background_color: "#0F172A", text_color: "#FFFFFF",
  btn_text_fr: "", btn_text_en: "", btn_url: "", btn_color: "#FFC928",
  overlay_opacity: 0.3, text_position: "left",
  is_active: false, start_date: "", end_date: "", status: "draft",
};

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPlacement, setFilterPlacement] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [langTab, setLangTab] = useState("en");

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/banners`, { withCredentials: true });
      setBanners(res.data ?? []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load banners");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBanners(); }, [fetchBanners]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const old = banners.findIndex(b => b.id === active.id);
      const nw = banners.findIndex(b => b.id === over.id);
      const reordered = arrayMove(banners, old, nw);
      setBanners(reordered);
      await axios.put(`${API}/admin/banners/reorder`, { order: reordered.map(b => b.id) }, { withCredentials: true });
    }
  };

  const createBanner = () => setEditModal({ ...emptyBanner, _isNew: true });

  const saveBanner = async () => {
    if (!editModal) return;
    try {
      const payload = { ...editModal };
      // Sync desktop_image to image for backward compat
      if (payload.desktop_image && !payload.image) payload.image = payload.desktop_image;
      if (payload._isNew) {
        const { _isNew, ...data } = payload;
        await axios.post(`${API}/admin/banners`, data, { withCredentials: true });
      } else {
        const { id, _isNew, created_at, created_by, version, display_order, ...data } = payload;
        await axios.put(`${API}/admin/banners/${editModal.id}`, data, { withCredentials: true });
      }
      setEditModal(null);
      fetchBanners();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to save banner");
    }
  };

  const toggleActive = async (banner) => {
    await axios.put(`${API}/admin/banners/${banner.id}`, { is_active: !banner.is_active }, { withCredentials: true });
    fetchBanners();
  };

  const duplicateBanner = async (banner) => {
    await axios.post(`${API}/admin/banners/${banner.id}/duplicate`, {}, { withCredentials: true });
    fetchBanners();
  };

  const deleteBanner = async (banner) => {
    if (!confirm(`Delete "${banner.name}"?`)) return;
    await axios.delete(`${API}/admin/banners/${banner.id}`, { withCredentials: true });
    fetchBanners();
  };

  const set = (key, val) => setEditModal(prev => ({ ...prev, [key]: val }));

  const sizeGuide = editModal ? getBannerSizeGuide(editModal.type, editModal.placement) : null;

  const filtered = banners.filter(b => {
    if (searchQuery && !b.name?.toLowerCase().includes(searchQuery.toLowerCase()) && !b.title_en?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filterPlacement && b.placement !== filterPlacement) return false;
    if (filterStatus && b.status !== filterStatus) return false;
    if (filterType && b.type !== filterType) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse" data-testid="banners-loading">
        <div className="flex items-center justify-between"><div className="h-5 w-48 bg-slate-800 rounded" /><div className="h-9 w-32 bg-slate-800 rounded-lg" /></div>
        <div className="flex gap-2"><div className="h-9 w-48 bg-slate-800 rounded-lg" /><div className="h-9 w-32 bg-slate-800 rounded-lg" /></div>
        {[1, 2, 3].map(i => <div key={i} className="h-20 bg-slate-800/50 rounded-xl border border-slate-700/30" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4" data-testid="banners-error">
        <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
          <Palette size={24} className="text-red-400" />
        </div>
        <h3 className="text-lg font-semibold text-white">Failed to load banners</h3>
        <p className="text-sm text-slate-400">{error}</p>
        <button onClick={fetchBanners} className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-500" data-testid="banners-retry-btn">Retry</button>
      </div>
    );
  }

  return (
    <div data-testid="admin-banners">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <p className="text-sm text-slate-500">{banners.length} banners total{filtered.length !== banners.length && ` · ${filtered.length} shown`}</p>
        <button onClick={createBanner}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 flex items-center gap-2"
          data-testid="add-banner-btn">
          <Plus size={14} /> Add Banner
        </button>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-grow max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search banners..."
            className="w-full pl-9 pr-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
            data-testid="banner-search-input" />
        </div>
        <select value={filterPlacement} onChange={e => setFilterPlacement(e.target.value)}
          className="px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-white" data-testid="banner-filter-placement">
          <option value="">All Placements</option>
          {PLACEMENTS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-white" data-testid="banner-filter-status">
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="scheduled">Scheduled</option>
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-white" data-testid="banner-filter-type">
          <option value="">All Types</option>
          {BANNER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {/* Banner List */}
      <div className="space-y-2" data-testid="banners-list">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={filtered.map(b => b.id)} strategy={verticalListSortingStrategy}>
            {filtered.map(b => (
              <SortableBannerCard key={b.id} banner={b}
                onEdit={() => setEditModal({ ...b, desktop_image: b.desktop_image || b.image || "" })}
                onDuplicate={() => duplicateBanner(b)}
                onDelete={() => deleteBanner(b)}
                onToggle={() => toggleActive(b)} />
            ))}
          </SortableContext>
        </DndContext>

        {banners.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-4" data-testid="banners-empty">
            <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700/50 flex items-center justify-center">
              <Palette size={28} className="text-slate-500" />
            </div>
            <div className="text-center">
              <h3 className="text-base font-semibold text-white mb-1">No promotional banners found</h3>
              <p className="text-sm text-slate-500 max-w-sm">Create a banner and choose where it should appear on your site.</p>
            </div>
            <button onClick={createBanner}
              className="px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 flex items-center gap-2"
              data-testid="add-first-banner-btn">
              <Plus size={14} /> Add First Banner
            </button>
          </div>
        )}
        {banners.length > 0 && filtered.length === 0 && (
          <div className="text-center py-12 text-slate-500 text-sm">No banners match the current filters.</div>
        )}
      </div>

      {/* Edit/Create Modal */}
      {editModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-3xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h3 className="text-base font-semibold text-white">{editModal._isNew ? "New Banner" : "Edit Banner"}</h3>
              <button onClick={() => setEditModal(null)} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-5 max-h-[75vh] overflow-y-auto">
              {/* Name */}
              <div>
                <label className="text-xs text-slate-500 mb-1.5 block">Banner Name</label>
                <input type="text" value={editModal.name || ""} onChange={e => set("name", e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-blue-500 focus:outline-none"
                  placeholder="e.g. Summer Sale Banner" data-testid="banner-name-input" />
              </div>

              {/* Type & Placement — changes update size guide dynamically */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Type</label>
                  <select value={editModal.type} onChange={e => set("type", e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" data-testid="banner-type-select">
                    {BANNER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Placement</label>
                  <select value={editModal.placement} onChange={e => set("placement", e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" data-testid="banner-placement-select">
                    {PLACEMENTS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Language Tabs */}
              <div className="flex gap-2">
                {["en", "fr"].map(l => (
                  <button key={l} onClick={() => setLangTab(l)} className={cn("px-4 py-2 rounded-lg text-sm font-medium",
                    langTab === l ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400")}>
                    {l === "en" ? "English" : "Français"}
                  </button>
                ))}
              </div>

              {/* Title & Description */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Title ({langTab.toUpperCase()})</label>
                  <input type="text" value={editModal[`title_${langTab}`] || ""} onChange={e => set(`title_${langTab}`, e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-blue-500 focus:outline-none"
                    data-testid="banner-title-input" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Description ({langTab.toUpperCase()})</label>
                  <textarea rows={2} value={editModal[`description_${langTab}`] || ""} onChange={e => set(`description_${langTab}`, e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
                </div>
              </div>

              {/* Responsive Banner Images with Dynamic Size Guide */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3">Banner Images</h4>
                <div className="space-y-4">
                  <ImageSourceSelector label="Desktop Banner" deviceLabel="Desktop"
                    value={editModal.desktop_image} onChange={v => set("desktop_image", v)}
                    sizeGuide={sizeGuide?.desktop} maxFileSize={3} />
                  <ImageSourceSelector label="Tablet Banner" deviceLabel="Tablet"
                    value={editModal.tablet_image} onChange={v => set("tablet_image", v)}
                    sizeGuide={sizeGuide?.tablet} maxFileSize={3} />
                  <ImageSourceSelector label="Mobile Banner" deviceLabel="Mobile"
                    value={editModal.mobile_image} onChange={v => set("mobile_image", v)}
                    sizeGuide={sizeGuide?.mobile} maxFileSize={2.5} />
                </div>
              </div>

              {/* Colors & Style */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Background Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={editModal.background_color || "#0F172A"} onChange={e => set("background_color", e.target.value)}
                      className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
                    <input type="text" value={editModal.background_color || ""} onChange={e => set("background_color", e.target.value)}
                      className="flex-1 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Text Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={editModal.text_color || "#FFFFFF"} onChange={e => set("text_color", e.target.value)}
                      className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
                    <input type="text" value={editModal.text_color || ""} onChange={e => set("text_color", e.target.value)}
                      className="flex-1 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Button Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={editModal.btn_color || "#FFC928"} onChange={e => set("btn_color", e.target.value)}
                      className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
                    <input type="text" value={editModal.btn_color || ""} onChange={e => set("btn_color", e.target.value)}
                      className="flex-1 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white" />
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Button Text ({langTab.toUpperCase()})</label>
                  <input type="text" value={editModal[`btn_text_${langTab}`] || ""} onChange={e => set(`btn_text_${langTab}`, e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Button URL</label>
                  <input type="text" value={editModal.btn_url || ""} onChange={e => set("btn_url", e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
                </div>
              </div>

              {/* Text Position */}
              <div>
                <label className="text-xs text-slate-500 mb-1.5 block">Text Position</label>
                <div className="flex gap-2">
                  {["left", "center", "right"].map(p => (
                    <button key={p} onClick={() => set("text_position", p)}
                      className={cn("px-4 py-2 rounded-lg text-sm capitalize",
                        editModal.text_position === p ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400")}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status & Scheduling */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Status</label>
                  <select value={editModal.status} onChange={e => set("status", e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" data-testid="banner-status-select">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="scheduled">Scheduled</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Start Date</label>
                  <input type="datetime-local" value={editModal.start_date?.slice(0, 16) || ""} onChange={e => set("start_date", e.target.value ? new Date(e.target.value).toISOString() : null)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">End Date</label>
                  <input type="datetime-local" value={editModal.end_date?.slice(0, 16) || ""} onChange={e => set("end_date", e.target.value ? new Date(e.target.value).toISOString() : null)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
                </div>
              </div>

              {/* Active toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editModal.is_active || false} onChange={e => set("is_active", e.target.checked)}
                  className="rounded border-slate-600 bg-slate-800 text-blue-500" />
                <span className="text-sm text-slate-300">Active</span>
              </label>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-800 flex justify-end gap-3">
              <button onClick={() => setEditModal(null)} className="px-4 py-2 rounded-lg bg-slate-800 text-slate-400 text-sm">Cancel</button>
              <button onClick={saveBanner}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-500"
                data-testid="save-banner-btn">
                <Save size={14} className="inline mr-1" /> {editModal._isNew ? "Create Banner" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
